import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ERROR_UNAUTHORIZED } from '@/constants/constant-routes';
import useApplyUITheme, { useSiteTheme } from '@/hooks/useApplyUITheme';
import { UISite } from '@/services/ui-customization.service';
interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireRoles?: string[];
  requireAnyRole?: string[];
  requireAllRoles?: string[];
  fallbackPath?: string;
}

interface LoadingSpinnerProps {
  message?: string;
  site?: UISite;

}

function LoadingSpinner({ message = 'Loading...', site = 'auth' }: LoadingSpinnerProps) {
  useApplyUITheme(site);
  const { saved } = useSiteTheme(site);

  const primaryColor = saved?.primary_color;
  const pageBg = saved?.page_background;

  return (
    <div
      className={`min-h-screen flex items-center justify-center transition-colors ${
        !pageBg ? 'bg-gray-50' : ''
      }`}
      style={pageBg ? { backgroundColor: pageBg } : undefined}
    >
      <div className="flex flex-col items-center space-y-4">
        <div
          className={`w-12 h-12 border-4 rounded-full animate-spin ${
            !primaryColor ? 'border-primary-200 border-t-primary-600' : ''
          }`}
          style={
            primaryColor
              ? {
                  borderColor: `${primaryColor}33`,
                  borderTopColor: primaryColor,
                }
              : undefined
          }
        />
        <p className="text-gray-600 text-sm font-medium">{message}</p>
      </div>
    </div>
  );
}

export function AuthGuard({ 
  children, 
  requireAuth = true,
  requireRoles = [],
  requireAnyRole = [],
  requireAllRoles = [],
  fallbackPath = '/login'
}: AuthGuardProps) {
  const { 
    user, 
    isAuthenticated, 
    isLoading, 
    hasRole, 
    hasAnyRole, 
    hasAllRoles 
  } = useAuth();
  const location = useLocation();

  const currentSite: UISite = location.pathname.startsWith('/admin') ? 'admin' : 'learner';

  if (isLoading) {
    return <LoadingSpinner message="Verifying authentication..." site={currentSite} />;
  }

  // If authentication is not required, render children
  if (!requireAuth) {
    return <>{children}</>;
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Save the attempted location for redirect after login
    const redirectTo = location.pathname !== '/' ? location.pathname + location.search : '/dashboard';
    
    // Clear any existing secure page from history and redirect to login
    window.history.replaceState(null, '', `${fallbackPath}?redirect=${encodeURIComponent(redirectTo)}`);
    
    return (
      <Navigate 
        to={`${fallbackPath}?redirect=${encodeURIComponent(redirectTo)}`} 
        replace 
      />
    );
  }

  // Check role-based permissions
  if (isAuthenticated && user) {
    // Check if user has specific roles
    if (requireRoles.length > 0) {
      const hasRequiredRole = requireRoles.some(role => hasRole(role));
      if (!hasRequiredRole) {
        return <Navigate to={ERROR_UNAUTHORIZED} replace />;
      }
    }

    // Check if user has any of the specified roles
    if (requireAnyRole.length > 0) {
      if (!hasAnyRole(requireAnyRole)) {
        return <Navigate to={ERROR_UNAUTHORIZED} replace />;
      }
    }

    // Check if user has all of the specified roles
    if (requireAllRoles.length > 0) {
      if (!hasAllRoles(requireAllRoles)) {
        return <Navigate to={ERROR_UNAUTHORIZED} replace />;
      }
    }
  }

  // All checks passed, render the protected content
  return <>{children}</>;
}

// Higher-order component for protecting routes
export function withAuthGuard<P extends object>(
  Component: React.ComponentType<P>,
  options: Omit<AuthGuardProps, 'children'> = {}
) {
  return function AuthGuardedComponent(props: P) {
    return (
      <AuthGuard {...options}>
        <Component {...props} />
      </AuthGuard>
    );
  };
}

// Specialized guards for common use cases
export function AdminGuard({ children }: { children: ReactNode }) {
  return (
    <AuthGuard requireAnyRole={['super_admin', 'admin']}>
      {children}
    </AuthGuard>
  );
}

export function ManagerGuard({ children }: { children: ReactNode }) {
  return (
    <AuthGuard requireAnyRole={['super_admin', 'admin', 'manager']}>
      {children}
    </AuthGuard>
  );
}

export function PublicRoute({ children }: { children: ReactNode }) {
  return (
    <AuthGuard requireAuth={false}>
      {children}
    </AuthGuard>
  );
}

// Component to redirect authenticated users away from auth pages
export function GuestGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

if (isLoading) {
    return <LoadingSpinner message="Checking authentication..." site="auth" />;
  }

  if (isAuthenticated) {
    // Get redirect URL from query params or default to dashboard
    const urlParams = new URLSearchParams(location.search);
    const redirectTo = urlParams.get('redirect') || '/dashboard';
    
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

export default AuthGuard;